import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  addAdjustmentAmount,
  getAdjustmentTypes,
  getDailySettlementStatistics,
  getPartnerSettlementAmountSummary,
  getPartnerSettlementStatistics,
  getSettlementAmountSummary,
  getSettlementStatistics,
  getSettlementStatisticsDetailSummary,
  postSettlementConfirm,
  uploadExcelEtcAdjustment,
  validateExcelEtcAdjustment,
} from '@/services/settlement/settlementManagement/api'
import {
  DailySettlementStatisticsReq,
  PartnerSettlementStatisticsReq,
  ServiceTypeKey,
  SettlementAmountSummaryReq,
} from '@/services/settlement/settlementManagement/types'
import { queryClient } from '@/utils/queryClient'
import useExcelDownload from '@/hooks/useDownloadExcel'

export const useGetSettlementStatistics = ({ settlementMonth, size, page, country }: ApiPaginationReq<SettlementAmountSummaryReq>) => {
  const queryKey = ['SETTLEMENT_STATISTICS', settlementMonth, size, page, country]

  const { data, ...rest } = useQuery({
    queryKey,
    queryFn: () => getSettlementStatistics({ settlementMonth, size, page, country }),
    select: ({ data }) => data,
  })

  return {
    ...data,
    items: data?.items || [],
    removeQuery: () => {
      queryClient.removeQueries({ queryKey })
    },
    ...rest,
  }
}

export const useGetSettlementAmountSummary = ({ settlementMonth, country }: SettlementAmountSummaryReq) => {
  const queryKey = ['SETTLEMENT_SUMMARY', settlementMonth, country]
  const queryResult = useQuery({
    queryKey,
    queryFn: () => getSettlementAmountSummary({ settlementMonth, country }),
    select: ({ data }) => data,
  })

  return {
    ...queryResult,
    removeQuery: () => {
      queryClient.removeQueries({ queryKey })
    },
  }
}

export const useExportExcelSettlementStatistics = () => {
  const excelDownload = useExcelDownload({ config: { method: 'get' } })
  return {
    isLoading: excelDownload.isLoading,
    onExportData: ({ settlementMonth, country }: SettlementAmountSummaryReq) => {
      excelDownload.onDownloadExcel('/api2/mass/v1/settlements/partners/summary/excel', { settlementMonth, country })
    },
  }
}

export const useGetDailySettlementStatistics = ({ partnerId, settlementMonth, country }: DailySettlementStatisticsReq, enabled: boolean) =>
  useQuery({
    queryKey: ['DAILY_SETTLEMENT_STATISTICS', partnerId, settlementMonth, country],
    queryFn: () => getDailySettlementStatistics({ partnerId, settlementMonth, country }),
    select: ({ data }) => data,
    enabled,
  })

export const useGetPartnerSettlementStatistics = (
  { partnerId, settlementMonth, serviceType, page, country, ...rest }: PartnerSettlementStatisticsReq,
  enabled: boolean,
) =>
  useQuery({
    queryKey: ['PARTNER_SETTLEMENT_STATISTICS', partnerId, settlementMonth, serviceType, page, country],
    queryFn: () => getPartnerSettlementStatistics({ partnerId, settlementMonth, serviceType, page, country, ...rest }),
    select: ({ data }) => data,
    enabled,
  })

export const useExportExcelDailySettlementStatistics = () => {
  const excelDownload = useExcelDownload({ config: { method: 'get' } })
  return {
    isLoading: excelDownload.isLoading,
    onExportData: ({ partnerId, settlementMonth, country }: DailySettlementStatisticsReq) => {
      excelDownload.onDownloadExcel(`/api2/mass/v1/settlements/partners/${partnerId}/daily/summary/excel`, { settlementMonth, country })
    },
  }
}

export const useExportExcelPartnerSettlementStatistics = () => {
  const excelDownload = useExcelDownload({ config: { method: 'get' } })
  return {
    isLoading: excelDownload.isLoading,
    onExportData: ({
      partnerId,
      settlementMonth,
      serviceType,
      country,
    }: Pick<PartnerSettlementStatisticsReq, 'partnerId' | 'settlementMonth' | 'serviceType' | 'country'>) => {
      excelDownload.onDownloadExcel(`/api2/mass/v1/settlements/partners/${partnerId}/daily/details/excel`, { settlementMonth, serviceType, country })
    },
  }
}

export const useGetPartnerSettlementAmountSummary = ({ partnerId, settlementMonth, country }: DailySettlementStatisticsReq) =>
  useQuery({
    queryKey: ['PARTNER_SETTLEMENT_SUMMARY', partnerId, settlementMonth, country],
    queryFn: () => getPartnerSettlementAmountSummary({ partnerId, settlementMonth, country }),
    select: ({ data }) => data,
  })

export const usePostSettlementConfirm = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: postSettlementConfirm,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['SETTLEMENT_STATISTICS'],
      })
      queryClient.invalidateQueries({
        queryKey: ['SETTLEMENT_SUMMARY'],
      })
      queryClient.invalidateQueries({
        queryKey: ['PARTNER_SETTLEMENT_SUMMARY'],
      })
    },
  })
}

export const useGetAdjustmentTypes = () => {
  const { data, ...rest } = useQuery({
    queryKey: ['ADJUSTMENT_TYPES'],
    queryFn: getAdjustmentTypes,
    select: ({ data }) => data.items.map((item) => ({ label: item.name, value: item.code })),
  })

  return {
    options: data || [],
    ...rest,
  }
}

export const useAddAdjustmentAmount = () =>
  useMutation({
    mutationFn: addAdjustmentAmount,
  })

export const useGetSettlementStatisticsDetailSummary = ({
  partnerId,
  settlementMonth,
  serviceType,
  country,
}: {
  partnerId: string
  settlementMonth: string
  serviceType: ServiceTypeKey
  country?: string
}) => {
  const { data, ...rest } = useQuery({
    queryKey: ['SETTLEMENT_STATISTICS_DETAIL_SUMMARY', partnerId, settlementMonth, serviceType, country],
    queryFn: () => getSettlementStatisticsDetailSummary({ partnerId, settlementMonth, serviceType, country }),
    select: ({ data }) => data,
    enabled: serviceType !== 'ALL',
  })

  return {
    data: serviceType === 'ALL' ? undefined : data,
    ...rest,
  }
}

export const useExportExcelTemplate = () => {
  const excelDownload = useExcelDownload({
    fileNamePrefix: '기타조정비_템플릿',
    config: { method: 'get' },
  })
  return {
    isLoading: excelDownload.isLoading,
    onDownloadTemplate: () => {
      excelDownload.onDownloadExcel(`/api2/mass/v1/etc-adjustment/excel-template`, {
        excelTemplateType: 'ETC_ADJUSTMENT',
      })
    },
  }
}

export const useValidateExcelEtcAdjustment = () => {
  return useMutation({
    mutationFn: validateExcelEtcAdjustment,
  })
}

export const useUploadExcelEtcAdjustment = () => {
  return useMutation({
    mutationFn: uploadExcelEtcAdjustment,
  })
}
